import { Dispatch } from 'react';
import { ActionCreator } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { apiClient, apiClientNoAuthorized } from '../../services/apiService';
import { SectionActionTypes, SectionActionTypeName } from './actionTypes';
import { ISectionsState } from './store';
import { Section, SectionTableSectionInfo } from './types';
import { ApiResponse, ApiResponsePresignedURL } from '../../api/types';
import { AxiosError } from 'axios';

export const fetchSectionsAction: ActionCreator<ThunkAction<
  Promise<any>,
  ISectionsState,
  null,
  SectionActionTypes
>> = () => async (dispatch: Dispatch<SectionActionTypes>) => {
  await fetchSections(dispatch);
};

const fetchSections = async (dispatch) => {
  dispatch({
    type: SectionActionTypeName.FETCH_ALL_REQUEST,
    loading: true,
  });
  await apiClient
    .get<SectionTableSectionInfo>(`/api/usersManagement/sections`)
    .then((res) => {
      dispatch({
        type: SectionActionTypeName.FETCH_ALL_SUCCESS,
        loading: false,
        industryInfo: res.data.industryInformation,
        sections: res.data.sectionTableInformationDTO,
        filterType: res.data.filterType,
      });
    })
    .catch((err) => {
      dispatch({
        type: SectionActionTypeName.FETCH_ALL_FAIL,
        loading: false,
      });
    });
};

export const fetchSectionDetail: ActionCreator<ThunkAction<
  Promise<void>,
  ISectionsState,
  null,
  SectionActionTypes
>> = (
  industry: string,
  portalName: string,
  webSectionDisplayName: string
) => async (dispatch: Dispatch<SectionActionTypes>) => {
  dispatch({
    type: SectionActionTypeName.FETCH_SECTION_DETAIL_REQUEST,
    loading: true,
  });
  await apiClient
    .get<Section>(
      `/api/usersManagement/sections/${industry}/${portalName}/${webSectionDisplayName}`
    )
    .then((res) => {
      dispatch({
        type: SectionActionTypeName.FETCH_SECTION_DETAIL_SUCCESS,
        loading: false,
        section: res.data,
      });
    })
    .catch((err) => {
      dispatch({
        type: SectionActionTypeName.FETCH_SECTION_DETAIL_FAIL,
        loading: false,
      });
    });
};

export const postSection: ActionCreator<ThunkAction<
  Promise<any>,
  ISectionsState,
  null,
  SectionActionTypes
>> = (section: Section) => async (dispatch: Dispatch<SectionActionTypes>) => {
  dispatch({
    type: SectionActionTypeName.ADD_SECTION_REQUEST,
    loading: true,
  });

  await apiClient
    .post<ApiResponsePresignedURL>(`/api/usersManagement/sections`, section)
    .then(async (res) => {
      dispatch({
        type: SectionActionTypeName.ADD_SECTION_SUCCESS,
        loading: false,
      });      
      await fetchSections(dispatch);
    })
    .catch((err) => {
      dispatch({
        type: SectionActionTypeName.ADD_SECTION_SUCCESS,
        loading: false,
      });
    });
};

export const deleteSection: ActionCreator<ThunkAction<
  Promise<any>,
  ISectionsState,
  null,
  SectionActionTypes
>> = (industry: string, portalName: string, webSectionName: string) => async (
  dispatch: Dispatch<SectionActionTypes>
) => {
  dispatch({
    type: SectionActionTypeName.DELETE_SECTION_REQUEST,
    loading: true,
  });
  await apiClient
    .delete<ApiResponse>(
      `/api/usersManagement/sections/${industry}/${portalName}/${webSectionName}`
    )
    .then((res) => {
      dispatch({
        type: SectionActionTypeName.DELETE_SECTION_SUCCESS,
        loading: false,
        status: res.status,
        message: res.data.errorDescription,
        commited: res.data.commited,
      });
    })
    .catch((err) => {
      dispatch({
        type: SectionActionTypeName.DELETE_SECTION_FAIL,
        loading: false,
        status: err?.status,
      });
    });
};

export const updateSection: ActionCreator<ThunkAction<
  Promise<any>,
  ISectionsState,
  null,
  SectionActionTypes
>> = (section: Section) => async (dispatch: Dispatch<SectionActionTypes>) => {
  dispatch({
    type: SectionActionTypeName.UPDATE_SECTION_DETAIL_REQUEST,
    loading: true,
  });

  await apiClient
    .put<ApiResponsePresignedURL>(`/api/usersManagement/sections/`, section)
    .then(async (res) => {
      dispatch({
        type: SectionActionTypeName.UPDATE_SECTION_DETAIL_SUCCESS,
        loading: false,
        status: res.status,
        message: res.data.errorDescription,
        commited: res.data.commited,
      });      
      await fetchSections(dispatch);
    })
    .catch((err: AxiosError) => {
      dispatch({
        type: SectionActionTypeName.UPDATE_SECTION_DETAIL_FAIL,
        loading: false,
        status: err?.response?.status || 400,
        message: 'Error updating section',
      });
    });
};

const postImageSigned = async (presignedURL: string, file: File) => {
  await apiClientNoAuthorized
    .put<any>(presignedURL, file, {
      headers: { 'Content-Type': file.type },
    })
    .catch((err: AxiosError) => {
      console.error(err);
    });
};

const createFormDataForSection = (section: Section): FormData => {
  const formData = new FormData();

  for (var key in section) {
    if (key === 'quickLinkImage' && section.addQuickLinks) {
      for (var imageKey in section[key]) {
        if (
          section[key]![imageKey] !== null &&
          section[key]![imageKey] !== undefined
        ) {
          formData.append(`quickLinkImage${imageKey}`, section[key]![imageKey]);
        }
      }
    } else {
      if (section[key] !== null && section[key] !== undefined) {
        formData.append(key, section[key]);
      }
    }
  }

  return formData;
};
